import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ArrowLeft, DollarSign, Award, TrendingUp, Clock, CheckCircle2, Plus, AlertCircle, Tag, GitMerge, StickyNote, Edit2, Send, Mail, Phone, Users, Home } from 'lucide-react';
import { EngagementBadge, EngagementBreakdown } from './donors/EngagementBadge';
import { PledgesTab } from './donors/PledgesTab';
import { ContactTab } from './donors/ContactTab';
import { EmailListEditor, AddressListEditor } from './donors/ContactFieldEditors';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const STAGE_COLORS = {
  prospect: 'bg-gray-200 text-gray-800',
  cultivating: 'bg-blue-100 text-blue-800',
  solicited: 'bg-purple-100 text-purple-800',
  donor: 'bg-emerald-100 text-emerald-800',
  steward: 'bg-teal-100 text-teal-800',
  lapsed: 'bg-red-100 text-red-800',
};

const ACTIVITY_ICON = {
  donor_created:    { icon: Plus,          color: 'text-emerald-600' },
  stage_changed:    { icon: TrendingUp,    color: 'text-purple-600' },
  tag_added:        { icon: Tag,           color: 'text-blue-600' },
  tag_removed:      { icon: Tag,           color: 'text-gray-500' },
  gift_added:       { icon: DollarSign,    color: 'text-emerald-600' },
  gift_acknowledged:{ icon: CheckCircle2,  color: 'text-emerald-700' },
  gift_deleted:     { icon: DollarSign,    color: 'text-red-500' },
  note_added:       { icon: StickyNote,    color: 'text-amber-600' },
  action_created:   { icon: Clock,         color: 'text-blue-600' },
  action_completed: { icon: CheckCircle2,  color: 'text-emerald-600' },
  donors_merged:    { icon: GitMerge,      color: 'text-purple-600' },
};

/**
 * Full-page donor profile: KPIs strip + 4 sub-tabs (Timeline, Gifts, Notes, Actions).
 * Route: /donors/:id (registered in App.js).
 */
export default function DonorProfilePage() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('timeline');
  const [ackBusy, setAckBusy] = useState({});
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/donors/${id}/profile`);
      setProfile(data);
    } catch (e) {
      toast.error('Failed to load donor profile');
      navigate('/donors');
    }
    setLoading(false);
  }, [api, id, navigate]);

  useEffect(() => { load(); }, [load]);

  const ackGift = async (gid) => {
    setAckBusy(b => ({ ...b, [gid]: true }));
    try { await api.put(`/donor-gifts/${gid}`, { acknowledged: true }); toast.success('Acknowledged'); load(); }
    catch { toast.error('Failed'); }
    setAckBusy(b => { const n = { ...b }; delete n[gid]; return n; });
  };
  const completeAction = async (aid) => {
    try { await api.put(`/donor-actions/${aid}`, { completed: true }); toast.success('Done'); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading || !profile) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;

  const { donor, gifts, activities, actions, stats } = profile;
  const openActions = actions.filter(a => !a.completed_at);
  const closedActions = actions.filter(a => a.completed_at);

  return (
    <div className="max-w-6xl mx-auto space-y-4" data-testid="donor-profile-page">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate('/donors')} data-testid="profile-back">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to donors
        </Button>
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="profile-edit-btn">
          <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit donor
        </Button>
        <div className="flex-1" />
        {donor.needs_review && (
          <Badge className="bg-orange-100 text-orange-800 border-0">
            <AlertCircle className="h-3 w-3 mr-1" /> Needs review · {(donor.missing_fields || []).join(', ')}
          </Badge>
        )}
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold" data-testid="profile-name">{donor.name}</h1>
              {donor.org && <p className="text-sm text-gray-600">{donor.org}</p>}
              <div className="flex items-center gap-3 mt-2 text-sm text-gray-600 flex-wrap">
                <Badge className={`${STAGE_COLORS[donor.pipeline_stage] || ''} border-0 capitalize`}>{donor.pipeline_stage}</Badge>
                {donor.engagement_bucket && <EngagementBadge score={donor.engagement_score} bucket={donor.engagement_bucket} showScore />}
                {donor.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {donor.email}{(donor.emails || []).length > 1 ? ` +${donor.emails.length - 1}` : ''}</span>}
                {donor.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {donor.phone}</span>}
                {donor.household_name && <span className="flex items-center gap-1" data-testid="header-household"><Home className="h-3.5 w-3.5" /> {donor.household_name}</span>}
              </div>
              {(donor.tags || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {donor.tags.map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:min-w-[420px]">
              <Stat icon={DollarSign} label="Total given" value={fmt$(stats.total_given)} />
              <Stat icon={Award}      label="Gifts"       value={stats.gift_count} />
              <Stat icon={TrendingUp} label="Avg gift"    value={fmt$(stats.avg_gift)} />
              <Stat icon={Clock}      label="Open tasks"  value={stats.open_actions} warn={stats.open_actions > 0} />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 mt-3 text-xs text-gray-500 gap-2">
            <div>First gift: <span className="font-medium text-gray-700">{stats.first_gift_date || '—'}</span></div>
            <div>Last gift: <span className="font-medium text-gray-700">{stats.last_gift_date || '—'}</span></div>
            <div>Biggest: <span className="font-medium text-gray-700">{fmt$(stats.biggest_gift)}</span></div>
            <div>Unacked: <span className={`font-medium ${stats.unacked_gifts > 0 ? 'text-amber-700' : 'text-gray-700'}`}>{stats.unacked_gifts}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b flex-wrap">
        {['timeline', 'gifts', 'pledges', 'contact', 'notes', 'actions', 'engagement'].map(t => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm capitalize border-b-2 transition-colors ${tab === t ? 'border-[#6B3FA0] text-[#6B3FA0] font-semibold' : 'border-transparent text-gray-600'}`}
                  data-testid={`profile-tab-${t}`}>
            {t}{t === 'actions' && openActions.length > 0 ? ` (${openActions.length})` : ''}
          </button>
        ))}
      </div>

      {tab === 'pledges' && <PledgesTab api={api} donorId={id} />}
      {tab === 'contact' && <ContactTab api={api} donor={donor} onChanged={load} />}
      {tab === 'engagement' && (
        <Card data-testid="profile-engagement">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <EngagementBadge score={donor.engagement_score} bucket={donor.engagement_bucket} size="lg" showScore />
              <Button size="sm" variant="outline" onClick={async () => { try { await api.post(`/donors/${id}/recompute-engagement`); toast.success('Rescored'); load(); } catch { toast.error('Failed'); } }}
                      data-testid="profile-rescore-btn">Recompute</Button>
            </div>
            {donor.engagement_detail ? <EngagementBreakdown detail={donor.engagement_detail} />
              : <p className="text-sm text-gray-500">Score not yet computed. Click Recompute to generate.</p>}
          </CardContent>
        </Card>
      )}

      {/* Timeline — unified constituent feed: logged activities + every gift
          (imported gifts without an activity entry are merged in) */}
      {tab === 'timeline' && (() => {
        const loggedGiftIds = new Set(activities.filter(a => a.kind === 'gift_added').map(a => a.payload?.gift_id).filter(Boolean));
        const syntheticGifts = gifts.filter(g => !loggedGiftIds.has(g.id)).map(g => ({
          id: `gift-${g.id}`,
          kind: 'gift_added',
          summary: `${fmt$(g.amount)} gift · ${g.campaign || 'no campaign'}`,
          at: `${g.date}T00:00:00Z`,
          actor: '',
          dateOnly: true,
        }));
        const timeline = [...activities, ...syntheticGifts].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
        return (
        <Card data-testid="profile-timeline">
          <CardContent className="p-4">
            {timeline.length === 0 ? <p className="text-sm text-gray-500">No activity yet — new events will appear here.</p> : (
              <ol className="relative border-l border-gray-200 ml-2 space-y-3">
                {timeline.map(a => {
                  const meta = ACTIVITY_ICON[a.kind] || { icon: Clock, color: 'text-gray-400' };
                  const Icon = meta.icon;
                  return (
                    <li key={a.id} className="ml-4">
                      <span className={`absolute -left-2 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-2 ring-white ${meta.color}`}>
                        <Icon className="h-3 w-3" />
                      </span>
                      <div className="text-sm">
                        <span className="font-medium">{a.summary}</span>
                        <span className="text-xs text-gray-500 ml-2">{a.dateOnly ? a.at.slice(0, 10) : new Date(a.at).toLocaleString()}</span>
                      </div>
                      {a.actor && <div className="text-[11px] text-gray-500">by {a.actor}</div>}
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>
        );
      })()}

      {/* Gifts */}
      {tab === 'gifts' && (
        <div className="grid gap-1.5">
          {gifts.length === 0 ? <Card><CardContent className="p-8 text-center text-gray-500">No gifts logged for this donor.</CardContent></Card>
            : gifts.map(g => (
              <Card key={g.id}>
                <CardContent className="p-3 flex items-center gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-gray-500">{g.date}</span>
                      {g.campaign && <Badge variant="outline" className="text-xs">{g.campaign}</Badge>}
                      <Badge variant="outline" className="text-xs">{g.gift_type}</Badge>
                      {g.acknowledged
                        ? <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" /> Acknowledged</Badge>
                        : <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Pending ack</Badge>}
                    </div>
                    {g.notes && <div className="text-xs text-gray-500 mt-0.5">{g.notes}</div>}
                  </div>
                  <span className="font-bold text-lg tabular-nums">{fmt$(g.amount)}</span>
                  {!g.acknowledged && (
                    <Button size="sm" onClick={() => ackGift(g.id)} disabled={!!ackBusy[g.id]}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`profile-ack-${g.id}`}>
                      {ackBusy[g.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Ack</>}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* Notes */}
      {tab === 'notes' && (
        <Card>
          <CardContent className="p-4">
            {donor.notes ? (
              <pre className="whitespace-pre-wrap text-sm font-sans">{donor.notes}</pre>
            ) : <p className="text-sm text-gray-500">No notes yet. Edit this donor from the list to add some.</p>}
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setEditOpen(true)}>
              <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit donor
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Edit dialog (used from the header button and the Notes tab) */}
      <ProfileEditDialog open={editOpen} onClose={() => setEditOpen(false)}
                         donor={donor} api={api} onSaved={load} />

      {/* Actions */}
      {tab === 'actions' && (
        <div className="space-y-3">
          <Section title="Open" testid="profile-actions-open">
            {openActions.length === 0 ? <EmptyRow>No open tasks.</EmptyRow>
              : openActions.map(a => (
                <TaskRow key={a.id} a={a} onComplete={() => completeAction(a.id)} />
              ))}
          </Section>
          <Section title="Completed" testid="profile-actions-completed">
            {closedActions.length === 0 ? <EmptyRow>No completed tasks yet.</EmptyRow>
              : closedActions.map(a => <TaskRow key={a.id} a={a} completed />)}
          </Section>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, warn }) {
  return (
    <div className={`border rounded p-2 ${warn ? 'bg-amber-50 border-amber-200' : ''}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase text-gray-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase text-gray-500 mb-1">{title}</div>
      <div className="grid gap-1">{children}</div>
    </div>
  );
}

function EmptyRow({ children }) {
  return <Card><CardContent className="p-4 text-sm text-gray-500 text-center">{children}</CardContent></Card>;
}

function TaskRow({ a, onComplete, completed }) {
  return (
    <Card>
      <CardContent className="p-2.5 flex items-center gap-2">
        {!completed && onComplete && (
          <Button size="sm" variant="outline" onClick={onComplete}><CheckCircle2 className="h-3.5 w-3.5" /></Button>
        )}
        {completed && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${completed ? 'line-through text-gray-400' : ''}`}>
            {a.kind.replace('_', ' ')} · {a.description}
          </div>
          {a.due_date && <div className="text-[11px] text-gray-500">due {a.due_date}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

const STAGES = ['prospect', 'cultivating', 'solicited', 'donor', 'steward', 'lapsed'];

/**
 * Inline edit dialog — structured contact editing: first/last name, multiple
 * emails and addresses (with a primary each), org/phone/stage/tags/notes.
 */
function ProfileEditDialog({ open, onClose, donor, api, onSaved }) {
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open && donor) {
      setForm({
        first_name: donor.first_name || '',
        last_name: donor.last_name || '',
        org: donor.org || '',
        phone: donor.phone || '',
        pipeline_stage: donor.pipeline_stage || 'prospect',
        tags: (donor.tags || []).join(', '),
        notes: donor.notes || '',
        emails: (donor.emails || []).map(e => ({ ...e })),
        addresses: (donor.addresses || []).map(a => ({ ...a })),
      });
    }
  }, [open, donor]);

  if (!open || !donor) return null;

  const save = async () => {
    if (!(form.first_name || '').trim() && !(form.last_name || '').trim()) {
      toast.error('First or last name is required'); return;
    }
    setBusy(true);
    try {
      await api.put(`/donors/${donor.id}`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        org: form.org,
        phone: form.phone,
        pipeline_stage: form.pipeline_stage,
        notes: form.notes,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        emails: (form.emails || []).filter(e => (e.email || '').trim()),
        addresses: form.addresses || [],
      });
      toast.success('Donor updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="profile-edit-dialog">
        <DialogHeader><DialogTitle>Edit donor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>First name *</Label>
              <Input value={form.first_name || ''} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                     data-testid="profile-edit-first-name" autoFocus />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={form.last_name || ''} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                     data-testid="profile-edit-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Org</Label>
              <Input value={form.org || ''} onChange={e => setForm(f => ({ ...f, org: e.target.value }))} /></div>
            <div><Label>Phone</Label>
              <Input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
          </div>
          <div><Label>Stage</Label>
            <select value={form.pipeline_stage || 'prospect'}
                    onChange={e => setForm(f => ({ ...f, pipeline_stage: e.target.value }))}
                    className="border rounded px-2 py-1.5 w-full text-sm capitalize"
                    data-testid="profile-edit-stage">
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><Label>Emails</Label>
            <EmailListEditor emails={form.emails || []} onChange={emails => setForm(f => ({ ...f, emails }))} /></div>
          <div><Label>Addresses</Label>
            <AddressListEditor addresses={form.addresses || []} onChange={addresses => setForm(f => ({ ...f, addresses }))} /></div>
          <div><Label>Tags (comma-separated)</Label>
            <Input value={form.tags || ''} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                   placeholder="e.g., major-gift, 2026-appeal" data-testid="profile-edit-tags" /></div>
          <div><Label>Notes</Label>
            <Textarea rows={3} value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="profile-edit-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
